import { Box } from '@mantine/core';
import React from 'react';
import { getFileTypeLabel } from 'src/data-capture/utils/documentCategory';
import { formatDate } from 'src/meetings/utils/date-utils';
import { UploadedFile } from '../../hooks/file-uploads/types';
import { useFileDragDrop } from '../context/FileDragDropContext';
import { createDragElement } from '../utils/createDragElement';
import DocumentCard, { DocumentCardTag } from './DocumentCard';
import EditDocumentDateChip from './EditDocumentDateChip';
import classes from '../FilesPage.module.css';

type DocumentsFileCardProps = {
  file: UploadedFile;
  highlighted: boolean;
  menuItems: React.ReactNode;
  onUpdateDate: (file: UploadedFile, date: Date) => void;
};

export default function DocumentsFileCard({
  file,
  highlighted,
  menuItems,
  onUpdateDate,
}: DocumentsFileCardProps) {
  const dragDropContext = useFileDragDrop();

  const typeLabel = getFileTypeLabel(file);
  const createdLabel = `Created: ${file.documentDate ? formatDate(file.documentDate) : 'no date detected'}`;
  const tags: DocumentCardTag[] = [
    ...(typeLabel ? [{ label: typeLabel }] : []),
    {
      label: createdLabel,
      node: (
        <EditDocumentDateChip
          label={createdLabel}
          value={file.documentDate ? new Date(file.documentDate) : null}
          maxDate={new Date(file.createdAt)}
          onChange={date => onUpdateDate(file, date)}
        />
      ),
    },
    { label: `Uploaded: ${formatDate(file.createdAt)}` },
  ];

  const draggable = !!dragDropContext;
  const isDragging = dragDropContext?.isDragging(file.id);

  const handleDragStart = (e: React.DragEvent) => {
    if (!dragDropContext) return;
    e.dataTransfer.effectAllowed = 'move';
    dragDropContext.setDraggedItem(file);
    createDragElement(file, e);
  };

  const handleDragEnd = () => {
    if (!dragDropContext) return;
    dragDropContext.setDraggedItem(null);
    dragDropContext.setIsDraggedOver(null);
  };

  return (
    <Box
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`${classes.draggableCard} ${isDragging ? classes.dragging : ''}`.trim()}
      data-testid="document-card-draggable"
    >
      <DocumentCard
        fileName={file.file.fileName}
        tags={tags}
        highlighted={highlighted}
        menuItems={menuItems}
      />
    </Box>
  );
}
